import { licensingEndpoints } from './endpoints';

export const COMMUNITY_KEYS_LOAD_FAILED_MESSAGE = 'Failed to load the Community keys.';
export const COMMUNITY_KEY_GENERATE_FAILED_MESSAGE = 'Failed to generate a Community key.';
export const COMMUNITY_KEYS_SORT = '-createdAt';

// rest-api answers the Community key routes with the `error` envelope ({ error: { code, message,
// details } }); its message is what the user reads, and the fallback covers a reply that carries
// none (a proxy error, a network failure).
const communityKeyErrorMessage = (error, fallback) => {
	const message = error?.response?.data?.error?.message;

	return typeof message === 'string' && message !== '' ? message : fallback;
};

export class LicensingService {
	constructor(restAxios) {
		this.restAxios = restAxios;
	}

	// /licensing/products carries currentUpgradeChannel but accepts no companyId filter, so the
	// channel used to be read one product at a time — and fanning that out across every machine
	// was exhausting rest-api's connection pool. The /licensing/products/batch endpoint takes a
	// comma-separated productIds query and omits unknown IDs silently, so one call replaces the
	// fan-out. Products that never synced come back with currentUpgradeChannel === null, and
	// omitted IDs leave their key absent from the map (consumers treat absence as unknown).
	// The same call carries each machine's add-ons (productAddons). An older rest-api omits the
	// field, which is reported as null so "unknown" stays distinguishable from "no add-ons" ([]).
	// It also carries each machine's base maintenance (maintenance: plan, start, end, renewals), which replaced
	// central-api's maintenanceServices: null is a machine without maintenance, and an older rest-api that omits
	// the field leaves it undefined ("unknown"). The endpoint has nothing to say about no products at all.
	getProductsBatch = (productIds) => {
		if (!productIds.length) {
			return Promise.resolve({});
		}

		return this.restAxios.get('/licensing/products/batch', { params: { productIds: productIds.join(',') } })
			.then((response) => {
				const byProductId = {};
				response.data.forEach((product) => {
					byProductId[product.id] = {
						currentUpgradeChannel: product.currentUpgradeChannel ?? null,
						productAddons: Array.isArray(product.productAddons) ? product.productAddons : null,
						maintenance: product.maintenance,
					};
				});
				return byProductId;
			});
	};

	// One page of the signed-in user's company's Community keys, newest first (SYN-3463). Central
	// scopes the list to the caller's company, so no companyId is sent. Each item carries the key
	// to copy, when it was made and, once a machine activated it, that machine's serial number and
	// the activation date (both null until then). A failure rejects with the message to show.
	getCommunityKeys = (page, pageSize) => {
		return this.restAxios.get(licensingEndpoints.communityKeys, { params: { page, pageSize, sort: COMMUNITY_KEYS_SORT } })
			.then((response) => {
				const items = Array.isArray(response.data?.items) ? response.data.items : [];
				const pagination = response.data?.pagination ?? { page, pageSize, totalItems: items.length, totalPages: 1 };

				return { items, pagination };
			})
			.catch((error) => {
				throw communityKeyErrorMessage(error, COMMUNITY_KEYS_LOAD_FAILED_MESSAGE);
			});
	};

	// Generates a Community key for the signed-in user's company. joinPreviewProgram is the page's
	// "Join the Preview Program" box, sent explicitly either way (Central reads an absent value as
	// true). The 201 reply is the key itself ({ id, key, createdAt }): there is no job to wait for,
	// and a failed call creates nothing. A failure rejects with the message to show.
	createCommunityKey = (joinPreviewProgram) => {
		return this.restAxios.post(licensingEndpoints.communityKeys, { joinPreviewProgram: Boolean(joinPreviewProgram) })
			.then((response) => {
				return response.data;
			})
			.catch((error) => {
				throw communityKeyErrorMessage(error, COMMUNITY_KEY_GENERATE_FAILED_MESSAGE);
			});
	};
}
